'use client';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useCartStore } from 'app/store/cart';
import useSWR from 'swr';
import { getShippingMethod } from 'app/apis/cart';
import { getAddressBook, getAddressForm, putAddressBook } from 'app/apis/address';
import { CACHE_QUERY_KEY } from 'app/constants';
import { ROLE, CART_ADDRESS_FORM_FIELD_NAME } from 'app/cart/constants';
import { useToast, useModal } from '@shared/ui';
import i18n from '@shared/i18n';
import { ValidationError, ERROR_CODE } from '@shared/services';
import { useAutocomplete, getAllowedAutoCompleteFields } from './useAutocomplete';
import { ErrorReturnType, handleSubmitError } from './errorHandler';

interface UseCartShippingProps extends Partial<GetCartAddressBookParams> {}

const getCartShippingMethodFetcher = async ([, ship_to]: [never, string]) => {
    return getShippingMethod({ ship_to });
};

const defaultShipCountry = 'TW';

export function useCartShipping(props: UseCartShippingProps = {}) {
    const { shipping_type = null, ship_from = null, ship_to = null } = props;

    const {
        shippingMethod,
        shippingType: cartStoreShippingType,
        shipTo: cartStoreShipTo,
        shipFrom: cartStoreShipFrom,
        completedAddressBook,
        completedCartItems,
        addressForm,
        addressFormFieldIndexMap,
        addressCountry,
        isReceiverAsBuyer,
        editingAddress,
        latestUpdateField,
        draftAddressBook,
        shippingProfileId: _shippingProfileId,
        updateShippingType,
        updateShipTo,
        updateShipFrom,
        updateAddressBook,
        updateAddressForm,
        updateShippingMethod,
        partialUpdateAddressForm,
        updateAddressFormFieldValue,
        getAddressFormFieldValue,
        updateIsReceiverAsBuyer,
        updateAddressCountry,
        updateEditingAddress,
        updateLatestUpdateField,
        updateShippingProfileId,
        syncCompletedToDraft,
        __testOnlyResetShippingState,
    } = useCartStore();

    const toast = useToast();

    const { modal } = useModal();

    const [error, setError] = useState<ErrorReturnType>({
        [ROLE.RECEIVER]: {},
        [ROLE.BUYER]: {},
    });

    const fieldsRef = useRef<{
        receiver: Partial<Record<keyof CartAddressDeliveryBase, HTMLInputElement>>;
        buyer: Partial<Record<keyof CartAddressDeliveryBase, HTMLInputElement>>;
    }>({
        receiver: {},
        buyer: {},
    });

    const [hasShownErrorModal, setHasShownErrorModal] = useState(false);

    useSWR(
        !!completedCartItems.length && cartStoreShipTo
            ? [CACHE_QUERY_KEY.cartShippingMethod, cartStoreShipTo]
            : null,
        getCartShippingMethodFetcher,
        {
            onSuccess: (resp) => {
                if (resp?.data) {
                    updateShippingMethod(resp.data);
                }
            },
            onError: (_error: unknown) => {
                if (_error instanceof ValidationError) {
                    if (
                        _error.context.error_code === ERROR_CODE.DATA_NOT_FOUND &&
                        !hasShownErrorModal
                    ) {
                        setHasShownErrorModal(true);

                        modal({
                            headerContent: i18n.gettext('賣家尚未設定運送方法'),
                            bodyContent: i18n.gettext('賣家尚未設定運送方法'),
                            showCloseButton: false,
                            showCancelButton: false,
                            onRespond: () => {},
                        });
                    }
                }
            },
        }
    );

    const shippingProfile = useMemo(() => {
        if (!shippingMethod || shippingMethod.length === 0 || !cartStoreShippingType) {
            return {} as CartShippingMethod['shipping_methods'][number];
        }

        return shippingMethod.reduce(
            (acc, shipping) => {
                return (
                    shipping.shipping_methods.find(
                        (method) => method.shipping_type === cartStoreShippingType
                    ) ?? acc
                );
            },
            {} as CartShippingMethod['shipping_methods'][number]
        );
    }, [shippingMethod, cartStoreShippingType]);

    const shipTo = cartStoreShipTo ?? completedAddressBook?.ship_to ?? null;

    const shipFrom = cartStoreShipFrom ?? completedAddressBook?.ship_from ?? null;

    const shippingType = cartStoreShippingType ?? shippingProfile?.shipping_type ?? null;

    const shippingProfileId = shippingProfile?.id ?? _shippingProfileId;

    useAutocomplete({
        enable: editingAddress,
        isBuyer: false,
        onChange: partialUpdateAddressForm,
        shipTo,
        shippingType,
        zipCode: getAddressFormFieldValue(
            ROLE.RECEIVER,
            CART_ADDRESS_FORM_FIELD_NAME.ZIP_CODE
        ),
        county: getAddressFormFieldValue(
            ROLE.RECEIVER,
            CART_ADDRESS_FORM_FIELD_NAME.COUNTY
        ),
        district: getAddressFormFieldValue(
            ROLE.RECEIVER,
            CART_ADDRESS_FORM_FIELD_NAME.DISTRICT
        ),
        road: getAddressFormFieldValue(ROLE.RECEIVER, CART_ADDRESS_FORM_FIELD_NAME.ROAD),
        allowedAutoCompleteFields: getAllowedAutoCompleteFields(
            ROLE.RECEIVER,
            addressForm
        ),
        address: getAddressFormFieldValue(
            ROLE.RECEIVER,
            CART_ADDRESS_FORM_FIELD_NAME.ADDRESS
        ),
        latestUpdateField,
        updateLatestUpdateField,
    });

    useAutocomplete({
        enable: !isReceiverAsBuyer && editingAddress,
        isBuyer: true,
        onChange: partialUpdateAddressForm,
        shippingType,
        shipTo: getAddressFormFieldValue(
            ROLE.BUYER,
            CART_ADDRESS_FORM_FIELD_NAME.COUNTRY
        ),
        zipCode: getAddressFormFieldValue(
            ROLE.BUYER,
            CART_ADDRESS_FORM_FIELD_NAME.ZIP_CODE
        ),
        county: getAddressFormFieldValue(ROLE.BUYER, CART_ADDRESS_FORM_FIELD_NAME.COUNTY),
        district: getAddressFormFieldValue(
            ROLE.BUYER,
            CART_ADDRESS_FORM_FIELD_NAME.DISTRICT
        ),
        road: getAddressFormFieldValue(ROLE.BUYER, CART_ADDRESS_FORM_FIELD_NAME.ROAD),
        address: getAddressFormFieldValue(
            ROLE.BUYER,
            CART_ADDRESS_FORM_FIELD_NAME.ADDRESS
        ),
        allowedAutoCompleteFields: getAllowedAutoCompleteFields(ROLE.BUYER, addressForm),
        latestUpdateField,
        updateLatestUpdateField,
    });

    const [isCalculating, setCalculating] = useState(false);

    const completedAddress = useMemo(() => {
        const _addressBook = completedAddressBook?.[ROLE.RECEIVER];

        if (!_addressBook) {
            return {
                address: '',
                receiver: '',
                email: '',
                shippingProfile,
            };
        }

        let address = `${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.COUNTY] ?? ''}${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.DISTRICT] ?? ''}${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.ROAD] ?? ''}`;

        if (
            CART_ADDRESS_FORM_FIELD_NAME.STORE in _addressBook &&
            _addressBook[CART_ADDRESS_FORM_FIELD_NAME.STORE]
        ) {
            address = `${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.STORE]}門市（${address}）`;
        }

        const receiver = `${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.NAME] ?? ''} (${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.PHONE_CODE] ?? ''}) ${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.PHONE] ?? ''}`;

        const email = _addressBook[CART_ADDRESS_FORM_FIELD_NAME.EMAIL] ?? '';

        if (_addressBook[CART_ADDRESS_FORM_FIELD_NAME.ADDRESS]) {
            address = _addressBook[CART_ADDRESS_FORM_FIELD_NAME.ADDRESS];
        }

        return {
            address,
            receiver,
            email,
            shippingProfile,
        };
    }, [completedAddressBook, shippingProfile]);

    const draftAddress = useMemo(() => {
        const _addressBook = draftAddressBook?.[ROLE.RECEIVER];

        if (!_addressBook) {
            return {
                address: '',
                receiver: '',
                email: '',
                shippingProfile,
            };
        }

        let address = `${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.COUNTY] ?? ''}${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.DISTRICT] ?? ''}${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.ROAD] ?? ''}`;

        if (
            CART_ADDRESS_FORM_FIELD_NAME.STORE in _addressBook &&
            _addressBook[CART_ADDRESS_FORM_FIELD_NAME.STORE]
        ) {
            address = `${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.STORE]}門市（${address}）`;
        }

        const receiver = `${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.NAME] ?? ''} (${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.PHONE_CODE] ?? ''}) ${_addressBook[CART_ADDRESS_FORM_FIELD_NAME.PHONE] ?? ''}`;

        const email = _addressBook[CART_ADDRESS_FORM_FIELD_NAME.EMAIL] ?? '';

        if (_addressBook[CART_ADDRESS_FORM_FIELD_NAME.ADDRESS]) {
            address = _addressBook[CART_ADDRESS_FORM_FIELD_NAME.ADDRESS];
        }

        return {
            address,
            receiver,
            email,
            shippingProfile,
        };
    }, [draftAddressBook, shippingProfile]);

    function registerFieldElement(
        role: ROLE,
        key: keyof CartAddressDeliveryBase,
        element: HTMLInputElement
    ) {
        fieldsRef.current[role][key] = element;
    }

    function getFieldElement(role: ROLE, key: keyof CartAddressDeliveryBase) {
        return fieldsRef.current?.[role]?.[key];
    }

    const fetchAddressForm = useCallback(
        async (role: ROLE, _shippingType: ShippingType, _shipTo: string) => {
            if (!_shippingType || !_shipTo) {
                return;
            }

            setCalculating(true);

            try {
                const resp = await getAddressForm({
                    role,
                    shipping_type: _shippingType,
                    ship_to: _shipTo,
                });

                if (resp?.data) {
                    updateAddressForm(role, resp.data);
                }
            } catch (_error) {
                console.error(_error);
            }

            setCalculating(false);
        },
        [updateAddressForm]
    );

    const handleUpdateAddressFormFieldValue = (
        role: ROLE,
        key: keyof CartAddressDeliveryBase,
        value: string
    ) => {
        if (key === CART_ADDRESS_FORM_FIELD_NAME.COUNTRY && role === ROLE.BUYER) {
            fetchAddressForm(role, shippingType, value);

            return;
        }

        updateAddressFormFieldValue(role, key, value);
    };

    async function applyAddress() {
        const payload = {
            shipping_type: shippingType,
            ship_to: shipTo,
            ship_from: shipFrom,
        } as PutCartAddressBookPayload;

        if (!addressForm?.[ROLE.RECEIVER]) {
            toast({
                title: i18n.gettext('Error: Please fill in the recipient address'),
                status: 'error',
            });

            return null;
        }

        const receiver = addressForm[ROLE.RECEIVER].reduce((acc, field) => {
            acc[field.key] = getAddressFormFieldValue(ROLE.RECEIVER, field.key) ?? null;

            return acc;
        }, {} as CartAddressDeliveryBase);

        payload.receiver = receiver;

        // 如果買家不是收件人，則需要填寫買家地址
        payload.buyer = isReceiverAsBuyer
            ? null
            : addressForm[ROLE.BUYER].reduce((acc, field) => {
                  acc[field.key] =
                      getAddressFormFieldValue(ROLE.BUYER, field.key) || null;

                  return acc;
              }, {} as CartAddressDeliveryBase);

        let resp: PutCartAddressBookResponse | null = null;

        try {
            resp = await putAddressBook(payload);

            if (resp?.data) {
                updateAddressBook(resp.data);
            }
        } catch (_error) {
            const { errors, role, field } = handleSubmitError(_error as Error);

            if (errors) {
                setError(errors);

                if (role && field) {
                    getFieldElement(role, field)?.scrollIntoView();
                }

                return null;
            }
        }

        return resp;
    }

    function resetError() {
        setError({
            [ROLE.RECEIVER]: {},
            [ROLE.BUYER]: {},
        });
    }

    useEffect(() => {
        updateShippingType(shipping_type);
        updateShipTo(ship_to || defaultShipCountry);
        updateShipFrom(ship_from || defaultShipCountry);
    }, [
        shipping_type,
        ship_to,
        ship_from,
        updateShippingType,
        updateShipTo,
        updateShipFrom,
    ]);

    useEffect(() => {
        if (!shippingType || !shipFrom || !shipTo) {
            return;
        }

        (async () => {
            setCalculating(true);

            try {
                const resp = await getAddressBook({
                    shipping_type: shippingType,
                    ship_from: shipFrom,
                    ship_to: shipTo,
                });

                updateAddressBook(resp?.data ?? null);
            } catch (_error) {
                console.error(_error);
            }

            setCalculating(false);
        })();
    }, [shippingType, shipFrom, shipTo, updateAddressBook]);

    useEffect(() => {
        if (isReceiverAsBuyer) {
            return;
        }

        fetchAddressForm(
            ROLE.BUYER,
            shippingType,
            getAddressFormFieldValue(ROLE.BUYER, CART_ADDRESS_FORM_FIELD_NAME.COUNTRY) ??
                defaultShipCountry
        );
    }, [isReceiverAsBuyer, shippingType, getAddressFormFieldValue, fetchAddressForm]);

    const state = {
        shippingType,
        shipTo,
        shipFrom,
        shippingProfileId,
        shippingMethod,
        addressFormFieldIndexMap,
        addressForm,
        isCalculating,
        isReceiverAsBuyer,
        addressCountry,
        // 組合完的資料，提供顯示
        completedAddress,
        // 確定有新增至 addressBook 的資料，有的話代表此地址已被存在 DB
        // checkout payload 會用到
        completedAddressBook,
        editingAddress,
        error,
        draftAddress,
        draftAddressBook,
    };

    const actions = {
        updateAddressBook,
        updateAddressForm,
        updateShippingType,
        updateShipTo,
        updateShipFrom,
        fetchAddressForm,
        updateAddressFormFieldValue: handleUpdateAddressFormFieldValue,
        getAddressFormFieldValue,
        updateIsReceiverAsBuyer,
        applyAddress,
        registerFieldElement,
        getFieldElement,
        updateAddressCountry,
        updateEditingAddress,
        updateLatestUpdateField,
        resetError,
        syncCompletedToDraft,
        updateShippingProfileId,
        __testOnlyResetShippingState,
    };

    return {
        state,
        actions,
    };
}

export type UseCartShippingReturn = ReturnType<typeof useCartShipping>;
